import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import AdminManagement from './AdminManagement.jsx';
import ExamplePhotoManager from './ExamplePhotoManager.jsx';
import './AdminPanel.css';

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [referralFilter, setReferralFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Modal states
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserFormData, setEditUserFormData] = useState({});
  const [editingUserId, setEditingUserId] = useState(null);
  const [selectedUserForRename, setSelectedUserForRename] = useState(null);
  const [userOldPhotos, setUserOldPhotos] = useState([]);
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const [newPhotoName, setNewPhotoName] = useState('');
  const [newCaptureDate, setNewCaptureDate] = useState('');

  // Metadata Preview Modal States
  const [showMetadataPreviewModal, setShowMetadataPreviewModal] = useState(false);
  const [metadataPreviewData, setMetadataPreviewData] = useState(null);
  const [selectedUserForDownload, setSelectedUserForDownload] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const [isMaster, setIsMaster] = useState(false);
  const [adminRole, setAdminRole] = useState('admin');
  const [activeTab, setActiveTab] = useState('users');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const sidebarRef = useRef(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllUsers();
      setUsers(data.users || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
      if (err.response?.status === 401) {
        apiService.adminLogout();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    const masterFlag = sessionStorage.getItem('isMasterAdmin') === 'true';
    setIsMaster(masterFlag);
    setAdminRole(sessionStorage.getItem('adminRole') || 'admin');

    loadUsers();
  }, [navigate, loadUsers]);

  const handleLogout = () => {
    apiService.adminLogout();
    navigate('/admin/login');
  };

  const handleDownloadZIPClick = async (userId) => {
    try {
      setSelectedUserForDownload(userId);
      setMetadataPreviewData(null);
      setShowMetadataPreviewModal(true);

      const metadata = await apiService.getUserExportMetadata(userId);

      if (metadata && metadata.historic_capture_dates) {
        Object.keys(metadata.historic_capture_dates).forEach(key => {
          let dateStr = metadata.historic_capture_dates[key];
          if (dateStr && typeof dateStr === 'string') {
            metadata.historic_capture_dates[key] = dateStr.split(' ')[0].replace(/:/g, '-');
          }
        });
      }

      setMetadataPreviewData(metadata);
    } catch (err) {
      alert('Failed to fetch user metadata. Ensure you are logged in as admin.');
      setShowMetadataPreviewModal(false);
    }
  };

  const handleMetadataChange = (e, field) => {
    setMetadataPreviewData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleHistoricDateChange = (e, filename) => {
    setMetadataPreviewData(prev => ({
      ...prev,
      historic_capture_dates: {
        ...prev.historic_capture_dates,
        [filename]: e.target.value
      }
    }));
  };

  const handleSaveAndDownloadZIP = async () => {
    try {
      setDownloadingZip(true);
      // Save metadata first
      await apiService.updateUserExportMetadata(selectedUserForDownload, metadataPreviewData);

      // Then download zip
      const blob = await apiService.exportUserData(selectedUserForDownload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user_${selectedUserForDownload}_data.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      setShowMetadataPreviewModal(false);
    } catch (err) {
      alert('Failed to save metadata or download ZIP.');
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleOpenRenameModal = async (userId) => {
    try {
      setSelectedUserForRename(userId);
      const status = await apiService.getUserStatus(userId);
      const photos = status.old_photo_status?.photos || [];
      photos.forEach(p => {
        if (p.validation_results?.exif_date && typeof p.validation_results.exif_date === 'string') {
          p.validation_results.exif_date = p.validation_results.exif_date.split(' ')[0].replace(/:/g, '-');
        }
      });
      setUserOldPhotos(photos);
      setShowRenameModal(true);
    } catch (err) {
      alert('Failed to load user photos.');
    }
  };

  const handleRenamePhoto = async (photoId) => {
    try {
      const updateData = {};
      if (newPhotoName) updateData.custom_filename = newPhotoName;
      if (newCaptureDate) updateData.capture_date = newCaptureDate;

      await apiService.renamePhoto(photoId, updateData);
      setEditingPhotoId(null);
      // Refresh the modal data
      const status = await apiService.getUserStatus(selectedUserForRename);
      setUserOldPhotos(status.old_photo_status?.photos || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;
    try {
      await apiService.deletePhoto(photoId);
      // Refresh the modal data to show updated filenames based on backend re-indexing
      const status = await apiService.getUserStatus(selectedUserForRename);
      setUserOldPhotos(status.old_photo_status?.photos || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete photo');
    }
  };

  const handleOpenEditUserModal = (user) => {
    setEditingUserId(user.id);
    setEditUserFormData({
      name: user.name,
      email: user.email,
      mobile_no: user.mobile_no,
      dob: user.dob,
      android_version: user.android_version,
      country: user.country || '',
      gender: user.gender 
        ? (user.gender.toLowerCase() === 'boy' ? 'Male' 
           : user.gender.toLowerCase() === 'girl' ? 'Female' 
           : user.gender) 
        : '',
      ethnicity: user.ethnicity || '',
      referral_by: user.referral_by || ''
    });
    setShowEditUserModal(true);
  };

  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    setEditUserFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateUserProfile(editingUserId, editUserFormData);
      setShowEditUserModal(false);
      loadUsers(); // Refresh the list
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update user profile.');
    }
  };

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAllUsers = (visibleUsers) => {
    const visibleIds = visibleUsers.map(u => u.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedUserIds.includes(id));
    if (allSelected) {
      setSelectedUserIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedUserIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) return;
    const confirmMsg = `Are you sure you want to delete ${selectedUserIds.length} selected user(s) and all their photos? This action cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setBulkDeleting(true);
      const res = await apiService.bulkDeleteUsers(selectedUserIds);
      alert(res.message || 'Users deleted successfully.');
      setSelectedUserIds([]);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete selected users.');
    } finally {
      setBulkDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return (
      <div style={{ lineHeight: 1.25, whiteSpace: 'nowrap' }}>
        <div>{d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</div>
        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      {/* Hamburger toggle — visible only on mobile */}
      <button
        className="sidebar-toggle-btn"
        onClick={() => setSidebarOpen(o => !o)}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? '✕' : '☰'}
      </button>

      {/* Overlay — closes sidebar when tapped on mobile */}
      <div
        className={`sidebar-overlay${sidebarOpen ? ' visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside ref={sidebarRef} className={`admin-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="admin-sidebar-brand">
          <h2>🛡️ Admin Panel</h2>
          <p>Welcome, {sessionStorage.getItem('adminUsername') || 'Admin'}</p>
        </div>

        <nav className="admin-sidebar-nav">
          <button
            className={`sidebar-nav-btn${activeTab === 'users' ? ' active' : ''}`}
            onClick={() => { setActiveTab('users'); setSidebarOpen(false); }}
          >
            <span className="nav-icon">👥</span> All Registered Users
          </button>
          {adminRole !== 'viewer' && (
            <button
              className={`sidebar-nav-btn${activeTab === 'photos' ? ' active' : ''}`}
              onClick={() => { setActiveTab('photos'); setSidebarOpen(false); }}
            >
              <span className="nav-icon">📸</span> Manage Example Photos
            </button>
          )}
          {isMaster && (
            <button
              className={`sidebar-nav-btn${activeTab === 'admins' ? ' active' : ''}`}
              onClick={() => { setActiveTab('admins'); setSidebarOpen(false); }}
            >
              <span className="nav-icon">🛡️</span> Manage Admins
            </button>
          )}
        </nav>

        <button onClick={handleLogout} className="sidebar-logout-btn">
          <span className="nav-icon">🚪</span> Logout
        </button>
      </aside>

      {/* Main Content */}
      <main className="admin-main-content">
        <div className="admin-main-header">
          <h2>
            {activeTab === 'users'  && 'All Registered Users'}
            {activeTab === 'photos' && 'Manage Example Photos'}
            {activeTab === 'admins' && 'Manage Administrator Accounts'}
          </h2>
        </div>

        {activeTab === 'admins' && isMaster && <AdminManagement />}
        {activeTab === 'photos' && <ExamplePhotoManager />}

        {activeTab === 'users' && (
          <>
            <div className="admin-search-container">
              <h3 className="section-title">All Registered Users</h3>
              <div className="search-filter-group">
                <div className="search-wrapper">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    className="referral-search-input"
                    placeholder="Filter by Referral Name..."
                    value={referralFilter}
                    onChange={(e) => setReferralFilter(e.target.value)}
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="status-filter-select"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">⏳ Pending Verification</option>
                  <option value="approved">✅ Approved</option>
                </select>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {(() => {
              const filteredUsers = users.filter(u => {
                const matchesReferral = !referralFilter || (u.referral_by && u.referral_by.toLowerCase().includes(referralFilter.toLowerCase()));
                const matchesStatus = !statusFilter || (statusFilter === 'approved' && u.is_approved) || (statusFilter === 'pending' && !u.is_approved);
                return matchesReferral && matchesStatus;
              });

              const allVisibleSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedUserIds.includes(u.id));

              return (
                <>
                  {isMaster && (
                    <div className="bulk-actions-toolbar">
                      <div className="bulk-actions-left">
                        <label className="bulk-select-all-label">
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={() => handleSelectAllUsers(filteredUsers)}
                            className="bulk-checkbox"
                          />
                          <span>Select All ({selectedUserIds.length} selected)</span>
                        </label>
                        {selectedUserIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedUserIds([])}
                            className="bulk-clear-btn"
                          >
                            Clear Selection
                          </button>
                        )}
                      </div>
                      {selectedUserIds.length > 0 && (
                        <button
                          type="button"
                          onClick={handleBulkDelete}
                          disabled={bulkDeleting}
                          className="bulk-delete-btn"
                        >
                          🗑️ {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedUserIds.length})`}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="users-table-container">
                    <table className="users-table">
                      <thead>
                        <tr>
                          {isMaster && (
                            <th className="th-select">
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                onChange={() => handleSelectAllUsers(filteredUsers)}
                                style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: '#ef4444' }}
                                title="Select / Deselect all"
                              />
                            </th>
                          )}
                          <th className="th-id">ID</th>
                          <th>Name</th>
                          {adminRole !== 'viewer' && <th>Email</th>}
                          <th>Referral</th>
                          <th>Android Ver.</th>
                          <th>Status</th>
                          <th>Photos Uploaded</th>
                          <th>Registration Date</th>
                          <th className="th-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan={isMaster ? (adminRole === 'viewer' ? '9' : '10') : (adminRole === 'viewer' ? '8' : '9')} className="empty-state">No users found.</td>
                          </tr>
                        ) : (
                          filteredUsers.map(user => (
                            <tr key={user.id} style={{ background: selectedUserIds.includes(user.id) ? '#fef2f2' : undefined }}>
                              {isMaster && (
                                <td data-label="Select" className="user-select-cell">
                                  <input
                                    type="checkbox"
                                    checked={selectedUserIds.includes(user.id)}
                                    onChange={() => handleSelectUser(user.id)}
                                    className="row-checkbox"
                                  />
                                </td>
                              )}
                              <td data-label="ID" className="user-id-cell"><span className="user-id-badge">#{user.id}</span></td>
                              <td data-label="Name" style={{ fontWeight: 600 }}>{user.name}</td>
                              {adminRole !== 'viewer' && <td data-label="Email">{user.email}</td>}
                              <td data-label="Referral">{user.referral_by || 'N/A'}</td>
                              <td data-label="Android Ver.">{user.android_version || 'N/A'}</td>
                        <td data-label="Status">
                          {user.is_approved ? (
                            <span className="badge badge-valid">✅ Approved</span>
                          ) : (
                            <span className="badge badge-pending">⏳ Pending</span>
                          )}
                        </td>
                        <td data-label="Photos">
                          <div className="photo-stats">
                            <span title="Old Photo" className={user.stats.old_photos > 0 ? 'stat-ok' : 'stat-missing'}>
                              O: {user.stats.old_photos}
                            </span>
                            <span title="Present Photos" className={user.stats.present_photos >= 6 ? 'stat-ok' : 'stat-missing'}>
                              P: {user.stats.present_photos}
                            </span>
                            {user.id % 2 !== 0 ? (
                              <span title="Variation A" className="stat-neutral">
                                A: {user.stats.post_a_photos}
                              </span>
                            ) : (
                              <span title="Variation B" className="stat-neutral">
                                B: {user.stats.post_b_photos}
                              </span>
                            )}
                            {user.stats.ai_photos_count > 0 && (
                              <span
                                title={`${user.stats.ai_photos_count} photo(s) flagged as AI generated`}
                                style={{ background: '#fee2e2', color: '#dc2626', fontWeight: 700, border: '1px solid #fca5a5', padding: '0.12rem 0.45rem', borderRadius: '4px', fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                              >
                                🤖 {user.stats.ai_photos_count} AI
                              </span>
                            )}
                          </div>
                        </td>
                        <td data-label="Date Joined">{formatDate(user.created_at)}</td>
                        <td data-label="Actions" className="user-actions-cell">
                          <div className="action-buttons-group">
                            <button
                              className="action-icon-btn"
                              title="Manage Photos"
                              onClick={() => window.open(`/dashboard/${encodeURIComponent(user.name)}`, '_blank')}
                            >
                              🖼️ Manage Photos
                            </button>
                            {adminRole !== 'viewer' && (
                              <>
                                <button
                                  className="action-icon-btn"
                                  title="Edit Metadata"
                                  style={{ background: '#f59e0b' }}
                                  onClick={() => handleOpenEditUserModal(user)}
                                >
                                  ✏️ Edit Metadata
                                </button>
                                <button
                                  className="action-icon-btn"
                                  title="Download ZIP"
                                  style={{ background: '#3b82f6' }}
                                  onClick={() => handleDownloadZIPClick(user.id)}
                                >
                                  📥 Download ZIP
                                </button>
                                {user.stats.old_photos > 0 && (
                                  <button
                                    className="action-icon-btn"
                                    title="Edit Historic Dates"
                                    style={{ background: '#64748b' }}
                                    onClick={() => handleOpenRenameModal(user.id)}
                                  >
                                    📅 Edit Historic Dates
                                  </button>
                                )}
                              </>
                            )}
                            {isMaster && (
                              <button
                                className="action-icon-btn"
                                title="Delete User"
                                style={{ background: '#ef4444' }}
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this user and all their photos? This action cannot be undone.')) {
                                    try {
                                      await apiService.deleteUser(user.id);
                                      loadUsers();
                                    } catch (err) {
                                      alert(err.response?.data?.error || 'Failed to delete user');
                                    }
                                  }
                                }}
                              >
                                🗑️ Delete User
                              </button>
                            )}
                            {user.is_approved && adminRole !== 'viewer' && (
                              <button
                                className="action-icon-btn"
                                title="Undo Approval"
                                style={{ background: '#f97316' }}
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to undo approval for this user? They will be allowed to re-upload photos.')) {
                                    try {
                                      await apiService.undoApproveUser(user.id);
                                      loadUsers();
                                    } catch (err) {
                                      alert(err.response?.data?.error || 'Failed to undo approval');
                                    }
                                  }
                                }}
                              >
                                ↩️ Undo Approval
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </>
        )}

        {/* Rename Photos Modal */}
        {showRenameModal && (
          <div className="modal-overlay" onClick={() => setShowRenameModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Rename Old Photos</h3>
                <button className="modal-close-btn" onClick={() => setShowRenameModal(false)}>&times;</button>
              </div>
              {userOldPhotos.length === 0 ? (
                <p>No old photos found.</p>
              ) : (
                userOldPhotos.map((photo, index) => (
                  <div key={photo.id} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                    <div style={{ position: 'relative', width: '100%', marginBottom: '0.5rem' }}>
                      {(photo.validation_results?.is_ai_generated || (photo.validation_results?.ai_confidence && photo.validation_results.ai_confidence >= 50)) && (
                        <div
                          className={`ai-confidence-badge ${(photo.validation_results?.ai_confidence || 0) >= 75 ? 'ai-high' : 'ai-moderate'}`}
                          style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10 }}
                          title={photo.validation_results?.ai_signals ? `AI Signals: ${photo.validation_results.ai_signals.join(', ')}` : 'AI Generated Photo'}
                        >
                          🤖 AI: {photo.validation_results.ai_confidence}% Sure
                        </div>
                      )}
                      <img src={apiService.getPhotoUrl(photo.url || photo.id)} alt="old" style={{ width: '100%', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer', display: 'block' }} onClick={() => setLightboxImage(apiService.getPhotoUrl(photo.url || photo.id))} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <h5 style={{ margin: 0 }}>{photo.custom_filename || `Old Photo ${index + 1}`}</h5>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Capture Date: {photo.validation_results?.exif_date || 'Unknown'}
                      </span>
                      <button onClick={() => {
                        setEditingPhotoId(photo.id);
                        setNewPhotoName(photo.custom_filename || `historic_${index + 1}.jpg`);
                        setNewCaptureDate(photo.validation_results?.exif_date || '');
                      }} style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', background: '#e2e8f0', color: '#1e293b', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}>✎ Edit</button>
                      <button onClick={() => handleDeletePhoto(photo.id)} style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>🗑️ Delete</button>
                    </div>
                    {editingPhotoId === photo.id && (
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1, minWidth: '140px' }}>
                          <label style={{ fontSize: '0.7rem', color: '#64748b' }}>Filename</label>
                          <input
                            type="text"
                            value={newPhotoName}
                            onChange={(e) => setNewPhotoName(e.target.value)}
                            style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px', width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1, minWidth: '140px' }}>
                          <label style={{ fontSize: '0.7rem', color: '#64748b' }}>Capture Date (YYYY-MM-DD)</label>
                          <input
                            type="text"
                            placeholder="e.g. 2026-05-15"
                            value={newCaptureDate}
                            onChange={(e) => setNewCaptureDate(e.target.value)}
                            style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px', width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '1rem' }}>
                          <button onClick={() => handleRenamePhoto(photo.id)} style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                          <button onClick={() => setEditingPhotoId(null)} style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', background: '#cbd5e1', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUserModal && (
          <div className="modal-overlay" onClick={() => setShowEditUserModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit User Metadata</h3>
                <button className="modal-close-btn" onClick={() => setShowEditUserModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleEditUserSubmit} className="registration-form">
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Name:</label>
                  <input type="text" name="name" value={editUserFormData.name || ''} onChange={handleEditUserChange} required style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Email:</label>
                  <input type="email" name="email" value={editUserFormData.email || ''} onChange={handleEditUserChange} required style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Mobile Number:</label>
                  <input type="tel" name="mobile_no" value={editUserFormData.mobile_no || ''} onChange={handleEditUserChange} required style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Date of Birth:</label>
                  <input type="date" name="dob" value={editUserFormData.dob || ''} onChange={handleEditUserChange} required style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Android Version:</label>
                  <input type="text" name="android_version" value={editUserFormData.android_version || ''} onChange={handleEditUserChange} required style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Country:</label>
                  <input type="text" name="country" value={editUserFormData.country || ''} onChange={handleEditUserChange} required style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Gender:</label>
                  <select name="gender" value={editUserFormData.gender || ''} onChange={handleEditUserChange} required style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                    <option value="">Select</option>
                    <option value="MALE">MALE</option>
                    <option value="FEMALE">FEMALE</option>
                    <option value="OTHER">OTHER</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Ethnicity:</label>
                  <input type="text" name="ethnicity" value={editUserFormData.ethnicity || ''} onChange={handleEditUserChange} required style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Referral Name:</label>
                  <input type="text" name="referral_by" value={editUserFormData.referral_by || ''} onChange={handleEditUserChange} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="upload-btn" style={{ background: '#10b981', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Changes</button>
                  <button type="button" onClick={() => setShowEditUserModal(false)} className="delete-btn" style={{ background: '#ef4444', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Metadata Preview / ZIP Modal */}
        {showMetadataPreviewModal && (
          <div className="modal-overlay" onClick={() => setShowMetadataPreviewModal(false)}>
            <div className="modal-content modal-content-lg" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Review &amp; Edit ZIP Metadata</h3>
                <button className="modal-close-btn" onClick={() => setShowMetadataPreviewModal(false)}>&times;</button>
              </div>

              {!metadataPreviewData ? (
                <p>Loading metadata...</p>
              ) : (
                <div className="registration-form">
                  <p style={{ marginBottom: '1rem', color: '#64748b', fontSize: '0.9rem' }}>
                    Please verify or edit the metadata below before downloading the ZIP file. Changes will be saved permanently.
                  </p>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>Country:</label>
                    <input type="text" value={metadataPreviewData.country || ''} onChange={(e) => handleMetadataChange(e, 'country')} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>Date of Birth (YYYY-MM):</label>
                    <input type="text" value={metadataPreviewData.date_of_birth || ''} onChange={(e) => handleMetadataChange(e, 'date_of_birth')} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>Gender:</label>
                    <select value={metadataPreviewData.gender || ''} onChange={(e) => handleMetadataChange(e, 'gender')} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                      <option value="">Select</option>
                      <option value="MALE">MALE</option>
                      <option value="FEMALE">FEMALE</option>
                      <option value="OTHER">OTHER</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>Ethnicity:</label>
                    <input type="text" value={metadataPreviewData.ethnicity || ''} onChange={(e) => handleMetadataChange(e, 'ethnicity')} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>Device OS:</label>
                    <input type="text" value={metadataPreviewData.device_os || ''} onChange={(e) => handleMetadataChange(e, 'device_os')} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                  </div>

                  <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                    <h4 style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>Historic Capture Dates</h4>
                    {Object.keys(metadataPreviewData.historic_capture_dates || {}).length === 0 ? (
                      <p style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '0.5rem' }}>No historic capture dates available.</p>
                    ) : (
                      <div style={{ marginTop: '0.5rem' }}>
                        {Object.entries(metadataPreviewData.historic_capture_dates).map(([filename, date]) => (
                          <div key={filename} className="form-group" style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <label style={{ minWidth: '120px', fontSize: '0.85rem', wordBreak: 'break-all' }}>{filename}:</label>
                            <input
                              type="text"
                              value={date || ''}
                              onChange={(e) => handleHistoricDateChange(e, filename)}
                              placeholder="YYYY-MM-DD"
                              style={{ flex: 1, minWidth: '140px', padding: '0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={handleSaveAndDownloadZIP}
                      disabled={downloadingZip}
                      style={{ background: '#3b82f6', color: 'white', padding: '0.75rem 1rem', border: 'none', borderRadius: '4px', cursor: downloadingZip ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
                    >
                      {downloadingZip ? 'Processing...' : '💾 Save & Download ZIP'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowMetadataPreviewModal(false)}
                      style={{ background: '#e2e8f0', color: '#1e293b', padding: '0.75rem 1rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Lightbox */}
        {lightboxImage && (
          <div className="lightbox-overlay" onClick={() => setLightboxImage(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, cursor: 'pointer' }}>
            <button className="lightbox-close" onClick={() => setLightboxImage(null)} style={{ position: 'absolute', top: '20px', right: '30px', background: 'none', border: 'none', color: 'white', fontSize: '3rem', cursor: 'pointer' }}>&times;</button>
            <div className="lightbox-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
              <img src={lightboxImage} alt="Fullscreen" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
